import { useEffect, useMemo, useState } from "react";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Modal, { ModalWidth } from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

const normalizeValue = (value: string | null) => value || "";

const AiKnowledgeModal = ({
  value,
  label,
  description,
  placeholder,
  onSave,
  onClose,
}: {
  value: string | null;
  label: string;
  description: string;
  placeholder: string;
  onSave: (jsonValue: string) => Promise<void>;
  onClose: () => void;
}) => {
  const savedValue = useMemo(() => normalizeValue(value), [value]);
  const [localValue, setLocalValue] = useState(savedValue);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = localValue !== savedValue;

  useEffect(() => {
    setLocalValue(savedValue);
  }, [savedValue]);

  const handleClose = () => {
    if (!isDirty || window.confirm("Discard changes?")) {
      onClose();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localValue);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open onClose={handleClose} width={ModalWidth.medium} alignedTop>
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <ModalTitle onClose={handleClose}>{label}</ModalTitle>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <TextareaWysiwyg
          value={savedValue}
          editable
          autofocus
          fullWidth
          placeholder={placeholder}
          deps={[savedValue]}
          className="not-prose min-h-[260px] max-h-[calc(100vh-20rem)] overflow-y-auto"
          onChangeValue={(jsonValue) => setLocalValue(jsonValue)}
        />
        <div className="flex items-center justify-end gap-2">
          <Button text="Cancel" onClick={handleClose} disabled={isSaving} />
          <Button
            text="Save"
            theme={buttonTheme.primary}
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          />
          {isSaving && <Loading mini size="4" />}
        </div>
      </div>
    </Modal>
  );
};

export default AiKnowledgeModal;
